import React, { useState, useEffect, useRef } from "react";
import { CameraOutlined, EditOutlined } from "@ant-design/icons";
import SocialIconRow from "../shared/SocialIconRow";
import SalutationPicker from "./SalutationPicker";

// Inline Editable Field Component
export const InlineEditableField = ({
    value,
    onChange,
    isVisible,
    placeholder,
    style,
    className = "",
    inputClassName = "",
    dir,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [localValue, setLocalValue] = useState(value || "");
    const inputRef = useRef(null);

    useEffect(() => {
        setLocalValue(value || "");
    }, [value]);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    if (!isVisible) return null;

    const handleBlur = () => {
        setIsEditing(false);
        if (localValue !== value) {
            onChange(localValue);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            handleBlur();
        }
        if (e.key === "Escape") {
            setLocalValue(value || "");
            setIsEditing(false);
        }
    };

    if (isEditing) {
        return (
            <input
                ref={inputRef}
                type="text"
                dir={dir}
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                style={{
                    ...style,
                    background: "rgba(255,255,255,0.1)",
                    border: "1px solid rgba(255,255,255,0.3)",
                    borderRadius: "4px",
                    padding: "4px 8px",
                    outline: "none",
                    textAlign: "center",
                    width: "100%",
                }}
                className={inputClassName}
            />
        );
    }

    return (
        <div
            dir={dir}
            onClick={() => setIsEditing(true)}
            style={style}
            className={`cursor-pointer hover:bg-white/10 active:bg-white/20 rounded px-2 py-1 transition-all duration-200 group relative ${className} ${!value ? 'border border-dashed border-white/30' : ''}`}
        >
            <span className={!value ? 'text-white/60' : ''}>{value || placeholder}</span>
            <EditOutlined style={{ marginInlineStart: 8 }} className="text-xs inline-block sm:opacity-0 sm:group-hover:opacity-100 opacity-40 transition-opacity" />
        </div>
    );
};

const getTextStyle = (cardData, field) => {
    const defaultStyles = { color: "#ffffff", size: "14", bold: false, italic: false };
    const fieldStyles = cardData?.textStyles?.[field] || defaultStyles;
    return {
        color: fieldStyles.color || defaultStyles.color,
        fontSize: `${fieldStyles.size || defaultStyles.size}px`,
        fontWeight: fieldStyles.bold ? "bold" : "normal",
        fontStyle: fieldStyles.italic ? "italic" : "normal",
    };
};

// One side of the card. `texts` holds the language-specific name, designation and
// company; phone, email and employee ID are shared between languages.
const HomeCardFace = ({
    cardData,
    texts,
    dir = "ltr",
    placeholders,
    onTextChange,
    onSharedChange,
    onLogoChange,
    onProfileChange,
    isImageLoading,
    imageType,
    socialLinks,
    salutation,
}) => (
    <div
        style={{
            backgroundColor: !cardData?.useBackgroundImage
                ? cardData?.backgroundColor || "#000147"
                : undefined,
            backgroundImage:
                cardData?.useBackgroundImage && cardData?.backgroundImageUrl
                    ? `url(${cardData.backgroundImageUrl})`
                    : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
        }}
        className="sm:rounded-lg lg:rounded-xl shadow-lg lg:shadow-xl overflow-hidden relative"
    >
        {/* Overlay */}
        <div
            className="absolute inset-0 bg-black opacity-40"
            style={{
                backgroundColor: !cardData?.useBackgroundImage
                    ? cardData?.backgroundColor || "#000147"
                    : undefined,
            }}
        />

        {/* Company Logo Section */}
        <div className="relative pt-4 sm:pt-6 lg:pt-8 xl:pt-10 pb-3 sm:pb-4 lg:pb-5 xl:pb-6">
            {(cardData?.companyLogoVisible ?? true) && (
                <div className="flex justify-center">
                    <label className="cursor-pointer group relative inline-block">
                        <input
                            type="file"
                            accept="image/*"
                            onChange={onLogoChange}
                            className="hidden"
                            disabled={isImageLoading}
                        />
                        <div className="relative">
                            <img
                                src={cardData?.companyLogoUrl || "/future_cards_logo.png"}
                                alt="Company Logo"
                                className="h-10 sm:h-12 lg:h-14 xl:h-16 object-contain transition-transform group-active:scale-95"
                                onError={(e) => (e.target.src = "/future_cards_logo.png")}
                            />
                            <div className="absolute inset-0 bg-black/40 rounded flex items-center justify-center opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity duration-200">
                                <CameraOutlined className="text-white text-lg sm:text-xl" />
                            </div>
                            {isImageLoading && imageType === 'logo' && (
                                <div className="absolute inset-0 bg-black/60 rounded flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent"></div>
                                </div>
                            )}
                        </div>
                    </label>
                </div>
            )}
        </div>

        {/* Profile Image - Centered, not overlapping */}
        <div className="relative px-4 sm:px-6 lg:px-8 xl:px-10 pb-4 sm:pb-6 lg:pb-8 xl:pb-10">
            {(cardData?.profileImageVisible ?? true) && (
                <div className="flex justify-center mb-3 sm:mb-4">
                    <label className="cursor-pointer group">
                        <input
                            type="file"
                            accept="image/*"
                            onChange={onProfileChange}
                            className="hidden"
                            disabled={isImageLoading}
                        />
                        <div className="relative">
                            <img
                                src={
                                    cardData?.profileImagePreview ||
                                    cardData?.profileImage ||
                                    (cardData?.gender === "Female" ? "/user_placeholder_women.png" : "/user-placeholder.png")
                                }
                                alt="Profile"
                                className="w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 xl:w-36 xl:h-36 rounded-full border-4 lg:border-[5px] border-white object-cover transition-transform group-active:scale-95"
                                onError={(e) => (e.target.src = cardData?.gender === "Female" ? "/user_placeholder_women.png" : "/user-placeholder.png")}
                            />
                            <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity duration-200">
                                <CameraOutlined className="text-white text-xl sm:text-2xl" />
                            </div>
                            {isImageLoading && imageType === 'profile' && (
                                <div className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent"></div>
                                </div>
                            )}
                        </div>
                    </label>
                </div>
            )}

            {/* Name & Designation */}
            <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                    {salutation && (
                        <SalutationPicker
                            value={salutation.value}
                            options={salutation.options}
                            onChange={salutation.onChange}
                            label={salutation.label}
                            emptyLabel={salutation.emptyLabel}
                        />
                    )}
                    <InlineEditableField
                        dir={dir}
                        value={texts?.name}
                        onChange={(val) => onTextChange("name", val)}
                        isVisible={cardData?.nameVisible ?? true}
                        placeholder={placeholders?.name}
                        style={getTextStyle(cardData, "name")}
                        className=""
                    />
                </div>
                <InlineEditableField
                    dir={dir}
                    value={texts?.designation}
                    onChange={(val) => onTextChange("designation", val)}
                    isVisible={cardData?.designationVisible ?? true}
                    placeholder={placeholders?.designation}
                    style={getTextStyle(cardData, "designation")}
                />
            </div>

            {/* Contact Details */}
            <div className="mt-6 space-y-1 text-center">
                <InlineEditableField
                    value={cardData?.phone}
                    onChange={(val) => onSharedChange("phone", val)}
                    isVisible={cardData?.phoneVisible ?? true}
                    placeholder={placeholders?.phone}
                    style={getTextStyle(cardData, "phone")}
                />
                <InlineEditableField
                    value={cardData?.email}
                    onChange={(val) => onSharedChange("email", val)}
                    isVisible={cardData?.emailVisible ?? true}
                    placeholder={placeholders?.email}
                    style={getTextStyle(cardData, "email")}
                />
                <InlineEditableField
                    value={cardData?.employeeId}
                    onChange={(val) => onSharedChange("employeeId", val)}
                    isVisible={cardData?.employeeIdVisible ?? true}
                    placeholder={placeholders?.employeeId}
                    style={getTextStyle(cardData, "employeeId")}
                />
            </div>

            {/* Divider */}
            <div
                className="mt-6 lg:mt-8 w-60 lg:w-72 xl:w-80 flex mx-auto border-t"
                style={{
                    borderTop: `2px solid ${cardData?.dividerColor || "#e5e7eb"}`,
                }}
            />

            {/* Company Name */}
            <div className="pt-4 mb-4">
                <InlineEditableField
                    dir={dir}
                    value={texts?.companyName}
                    onChange={(val) => onTextChange("companyName", val)}
                    isVisible={cardData?.companyNameVisible ?? true}
                    placeholder={placeholders?.companyName}
                    style={getTextStyle(cardData, "companyName")}
                    className="text-center"
                />
            </div>

            {/* Social profiles: a preview of the icons visitors will see on the public card */}
            <SocialIconRow
                links={socialLinks}
                interactive={false}
                color={cardData?.textStyles?.companyName?.color || "#ffffff"}
                className="mb-4"
            />

            {/* QR Code */}
            {(cardData?.qrCodeVisible ?? true) && (
                <div className="flex justify-center">
                    <img
                        src={cardData?.qrCodeUrl || "/sample_qr.svg"}
                        alt="QR Code"
                        className="w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28 xl:w-32 xl:h-32"
                        onError={(e) => (e.target.style.display = "none")}
                    />
                </div>
            )}
        </div>
    </div>
);

export default HomeCardFace;
